import React, { useState, useEffect } from "react";
import axios from "axios";
import AddUserByIdModal from "./AddUserByIdModal";

const BusinessUserManagement = ({ user }) => {
  const [businessUsers, setBusinessUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: "",
    relationshipType: "",
    page: 1,
    limit: 10,
  });

  const businessToken = localStorage.getItem("token");

  useEffect(() => {
    fetchBusinessUsers();
    fetchStats();
  }, [filters]);

  const fetchBusinessUsers = async () => {
    setLoading(true);
    setError("");

    try {
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append("status", filters.status);
      if (filters.relationshipType)
        queryParams.append("relationshipType", filters.relationshipType);
      queryParams.append("page", filters.page);
      queryParams.append("limit", filters.limit);

      const response = await axios.get(
        `https://dailybackend-zvvt.onrender.com/api/business-user-relations/users?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${businessToken}` },
        }
      );

      setBusinessUsers(response.data.relations);
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching business users");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        "https://dailybackend-zvvt.onrender.com/api/business-user-relations/stats",
        {
          headers: { Authorization: `Bearer ${businessToken}` },
        }
      );
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const handleUserAdded = (newRelation) => {
    setBusinessUsers((prev) => [newRelation, ...prev]);
    fetchStats(); // Refresh stats
  };

  const handleRemoveUser = async (relationId) => {
    if (
      !window.confirm(
        "Are you sure you want to remove this user from your business?"
      )
    ) {
      return;
    }

    try {
      await axios.delete(
        `https://dailybackend-zvvt.onrender.com/api/business-user-relations/users/${relationId}`,
        {
          headers: { Authorization: `Bearer ${businessToken}` },
        }
      );

      setBusinessUsers((prev) =>
        prev.filter((relation) => relation._id !== relationId)
      );
      fetchStats(); // Refresh stats
    } catch (err) {
      setError(err.response?.data?.message || "Error removing user");
    }
  };

  const handleStatusChange = async (relationId, newStatus) => {
    try {
      const response = await axios.put(
        `https://dailybackend-zvvt.onrender.com/api/business-user-relations/users/${relationId}`,
        { status: newStatus },
        {
          headers: { Authorization: `Bearer ${businessToken}` },
        }
      );

      setBusinessUsers((prev) =>
        prev.map((relation) =>
          relation._id === relationId ? response.data.relation : relation
        )
      );
    } catch (err) {
      setError(err.response?.data?.message || "Error updating status");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "#28a745";
      case "inactive":
        return "#6c757d";
      case "pending":
        return "#ffc107";
      case "suspended":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  const getRelationshipIcon = (type) => {
    switch (type) {
      case "customer":
        return "🛒";
      case "member":
        return "👤";
      case "subscriber":
        return "📧";
      case "client":
        return "💼";
      default:
        return "👤";
    }
  };

  return (
    <div className="business-user-management">
      <div className="header-section" style={{ marginBottom: "30px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2>User Management</h2>
          <button
            className="btn btn-primary"
            onClick={() => setShowAddUserModal(true)}
          >
            ➕ Add User by ID
          </button>
        </div>

        {stats && (
          <div
            className="stats-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "15px",
              marginTop: "20px",
            }}
          >
            <div
              className="stat-card"
              style={{
                backgroundColor: "#e3f2fd",
                padding: "15px",
                borderRadius: "8px",
              }}
            >
              <h4 style={{ margin: 0, color: "#1976d2" }}>Total Users</h4>
              <p
                style={{
                  margin: "5px 0 0",
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#1976d2",
                }}
              >
                {stats.totalUsers}
              </p>
            </div>
            <div
              className="stat-card"
              style={{
                backgroundColor: "#e8f5e9",
                padding: "15px",
                borderRadius: "8px",
              }}
            >
              <h4 style={{ margin: 0, color: "#388e3c" }}>Active Users</h4>
              <p
                style={{
                  margin: "5px 0 0",
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#388e3c",
                }}
              >
                {stats.activeUsers}
              </p>
            </div>
            <div
              className="stat-card"
              style={{
                backgroundColor: "#fff3e0",
                padding: "15px",
                borderRadius: "8px",
              }}
            >
              <h4 style={{ margin: 0, color: "#f57c00" }}>Recently Added</h4>
              <p
                style={{
                  margin: "5px 0 0",
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#f57c00",
                }}
              >
                {stats.recentlyAdded}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="filters-section" style={{ marginBottom: "20px" }}>
        <div
          style={{
            display: "flex",
            gap: "15px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div>
            <label style={{ marginRight: "5px" }}>Status:</label>
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  status: e.target.value,
                  page: 1,
                }))
              }
              style={{
                padding: "5px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <div>
            <label style={{ marginRight: "5px" }}>Type:</label>
            <select
              value={filters.relationshipType}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  relationshipType: e.target.value,
                  page: 1,
                }))
              }
              style={{
                padding: "5px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            >
              <option value="">All Types</option>
              <option value="customer">Customer</option>
              <option value="member">Member</option>
              <option value="subscriber">Subscriber</option>
              <option value="client">Client</option>
            </select>
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() =>
              setFilters({
                status: "",
                relationshipType: "",
                page: 1,
                limit: 10,
              })
            }
          >
            Clear Filters
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p>Loading users...</p>
        </div>
      ) : businessUsers.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
          }}
        >
          <h3>No Users Found</h3>
          <p>You haven't added any users to your business yet.</p>
          <button
            className="btn btn-primary"
            onClick={() => setShowAddUserModal(true)}
          >
            Add Your First User
          </button>
        </div>
      ) : (
        <div className="users-list">
          {businessUsers
            .filter((relation) => relation.userId) // Filter out relations without user data
            .map((relation) => (
              <div
                key={relation._id}
                className="user-card"
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  padding: "20px",
                  marginBottom: "15px",
                  backgroundColor: "white",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <span style={{ fontSize: "20px", marginRight: "10px" }}>
                        {getRelationshipIcon(relation.relationshipType)}
                      </span>
                      <div>
                        <h4 style={{ margin: 0 }}>
                          {relation.userId?.firstName || "Unknown"}{" "}
                          {relation.userId?.lastName || "User"}
                        </h4>
                        <p
                          style={{
                            margin: "2px 0",
                            color: "#666",
                            fontSize: "14px",
                          }}
                        >
                          @{relation.userId?.username || "unknown"} •{" "}
                          {relation.userId?.email || "No email"}
                        </p>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: "20px",
                        marginBottom: "10px",
                      }}
                    >
                      <div>
                        <strong>Unique ID:</strong>
                        <span
                          style={{
                            marginLeft: "5px",
                            fontFamily: "monospace",
                            backgroundColor: "#e9ecef",
                            padding: "2px 6px",
                            borderRadius: "4px",
                          }}
                        >
                          {relation.userUniqueId}
                        </span>
                      </div>
                      <div>
                        <strong>Type:</strong> {relation.relationshipType}
                      </div>
                      <div>
                        <strong>Added:</strong>{" "}
                        {new Date(relation.addedDate).toLocaleDateString()}
                      </div>
                    </div>

                    {relation.notes && (
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Notes:</strong> {relation.notes}
                      </div>
                    )}

                    <div
                      style={{ display: "flex", gap: "10px", fontSize: "12px" }}
                    >
                      <span
                        style={{
                          color: relation.permissions.viewProfile
                            ? "#28a745"
                            : "#dc3545",
                        }}
                      >
                        {relation.permissions.viewProfile ? "✓" : "✗"} Profile
                        Access
                      </span>
                      <span
                        style={{
                          color: relation.permissions.viewOrders
                            ? "#28a745"
                            : "#dc3545",
                        }}
                      >
                        {relation.permissions.viewOrders ? "✓" : "✗"} Order
                        Access
                      </span>
                      <span
                        style={{
                          color: relation.permissions.sendNotifications
                            ? "#28a745"
                            : "#dc3545",
                        }}
                      >
                        {relation.permissions.sendNotifications ? "✓" : "✗"}{" "}
                        Notifications
                      </span>
                    </div>
                  </div>{" "}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: "12px",
                      minWidth: "120px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          padding: "4px 8px",
                          borderRadius: "12px",
                          backgroundColor: `${getStatusColor(
                            relation.status
                          )}15`,
                          border: `1px solid ${getStatusColor(
                            relation.status
                          )}30`,
                        }}
                      >
                        <span
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: getStatusColor(relation.status),
                            display: "inline-block",
                          }}
                        ></span>
                        <span
                          style={{
                            color: getStatusColor(relation.status),
                            fontWeight: "600",
                            fontSize: "12px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {relation.status}
                        </span>
                      </div>
                      <select
                        value={relation.status}
                        onChange={(e) =>
                          handleStatusChange(relation._id, e.target.value)
                        }
                        style={{
                          padding: "6px 12px",
                          fontSize: "13px",
                          borderRadius: "6px",
                          border: `2px solid ${getStatusColor(
                            relation.status
                          )}`,
                          backgroundColor: `${getStatusColor(
                            relation.status
                          )}15`,
                          color: getStatusColor(relation.status),
                          fontWeight: "500",
                          cursor: "pointer",
                          outline: "none",
                          transition: "all 0.2s ease",
                          minWidth: "100px",
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = `${getStatusColor(
                            relation.status
                          )}25`;
                          e.target.style.transform = "translateY(-1px)";
                          e.target.style.boxShadow = `0 2px 8px ${getStatusColor(
                            relation.status
                          )}30`;
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = `${getStatusColor(
                            relation.status
                          )}15`;
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "none";
                        }}
                      >
                        <option
                          value="active"
                          style={{
                            backgroundColor: "#e8f5e9",
                            color: "#28a745",
                            padding: "5px",
                          }}
                        >
                          ✅ Active
                        </option>
                        <option
                          value="inactive"
                          style={{
                            backgroundColor: "#f5f5f5",
                            color: "#6c757d",
                            padding: "5px",
                          }}
                        >
                          ⏸️ Inactive
                        </option>
                        <option
                          value="pending"
                          style={{
                            backgroundColor: "#fff8e1",
                            color: "#ffc107",
                            padding: "5px",
                          }}
                        >
                          ⏳ Pending
                        </option>
                        <option
                          value="suspended"
                          style={{
                            backgroundColor: "#ffebee",
                            color: "#dc3545",
                            padding: "5px",
                          }}
                        >
                          🚫 Suspended
                        </option>
                      </select>
                    </div>{" "}
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleRemoveUser(relation._id)}
                      style={{
                        fontSize: "12px",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        border: "2px solid #dc3545",
                        backgroundColor: "#dc354515",
                        color: "#dc3545",
                        fontWeight: "500",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = "#dc3545";
                        e.target.style.color = "white";
                        e.target.style.transform = "translateY(-1px)";
                        e.target.style.boxShadow = "0 2px 8px #dc354530";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = "#dc354515";
                        e.target.style.color = "#dc3545";
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = "none";
                      }}
                    >
                      🗑️ Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {showAddUserModal && (
        <AddUserByIdModal
          onClose={() => setShowAddUserModal(false)}
          onUserAdded={handleUserAdded}
          businessToken={businessToken}
        />
      )}
    </div>
  );
};

export default BusinessUserManagement;
